import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CURRENT_VALUATIONS_SLUG,
  OPPORTUNITIES_SLUG,
  PAST_VALUATIONS_SLUG,
} from 'dashboard409a/common/constants/urls';
import { DEFAULT_VIEW_ID } from 'dashboard409a/pages/Workboard/common/constants';
import {
  createValuationsTableView,
  deleteValuationsTableView,
  listValuationsTableViews,
  updateValuationsTableView,
} from 'dashboard409a/services/workboardTableViews';
import {
  getWorkboardValuationsViewsQueryKey,
  ValuationView,
  WORKBOARD_VALUATIONS_VIEWS_MIGRATED_KEY,
} from 'dashboard409a/types/workboardTableViews/types';
import {
  fromValuationTableView,
  getValuationViewSectionId,
  normalizeTableDefinition,
  toValuationTableViewCreatePayload,
  toValuationTableViewUpdatePayload,
} from 'dashboard409a/utilities/valuationsViewConverter';

const VALUATION_VIEW_SECTIONS = [
  CURRENT_VALUATIONS_SLUG,
  PAST_VALUATIONS_SLUG,
  OPPORTUNITIES_SLUG,
] as const;

const LEGACY_VIEW_KEY_PREFIX = 'workboard_views_';

interface LegacyWorkboardView {
  id: string;
  name: string;
  columnVisibility?: Record<string, boolean>;
  filters?: {
    groupByDeals?: boolean;
  };
}

const normalizeLegacyView = (raw: ValuationView | LegacyWorkboardView): ValuationView => {
  if ('filters' in raw && raw.filters) {
    return {
      id: raw.id,
      name: raw.name,
      columnVisibility: raw.columnVisibility,
      groupByDeals: raw.filters.groupByDeals ?? false,
    };
  }

  return raw;
};

const migrateLocalValuationViewsIfNeeded = async (): Promise<void> => {
  const { getPreference, migrateLegacyLocalStorageKey, removePreference, setPreference } = await import(
    'services/storage/preferencesIndexedDB'
  );

  const alreadyMigrated = await getPreference<boolean>(WORKBOARD_VALUATIONS_VIEWS_MIGRATED_KEY);
  if (alreadyMigrated) {
    return;
  }

  const migrationResults = await VALUATION_VIEW_SECTIONS.reduce<Promise<PromiseSettledResult<unknown>[]>>(
    async (resultsPromise, sectionId) => {
      const results = await resultsPromise;
      const legacyKey = `${LEGACY_VIEW_KEY_PREFIX}${sectionId}`;
      await migrateLegacyLocalStorageKey(legacyKey);
      const localViews = await getPreference<(ValuationView | LegacyWorkboardView)[]>(legacyKey);
      const userViews = (localViews ?? [])
        .filter(view => view.id !== DEFAULT_VIEW_ID)
        .map(normalizeLegacyView);

      if (userViews.length === 0) {
        return results;
      }

      const sectionResults = await Promise.allSettled(
        userViews.map(view =>
          createValuationsTableView(toValuationTableViewCreatePayload(view, sectionId))
        )
      );

      if (sectionResults.every(result => result.status === 'fulfilled')) {
        await removePreference(legacyKey);
      }

      return [...results, ...sectionResults];
    },
    Promise.resolve([])
  );

  if (migrationResults.length === 0 || migrationResults.every(result => result.status === 'fulfilled')) {
    await setPreference(WORKBOARD_VALUATIONS_VIEWS_MIGRATED_KEY, true);
  }
};

const matchesSection = (tableView: { table_definition?: unknown }, sectionId?: string): boolean => {
  if (!sectionId) {
    return true;
  }

  const definition = normalizeTableDefinition(
    tableView.table_definition as Parameters<typeof normalizeTableDefinition>[0]
  );
  return getValuationViewSectionId(definition) === sectionId;
};

export const useWorkboardValuationsTableViews = (sectionId?: string) => {
  const queryClient = useQueryClient();
  const queryKey = getWorkboardValuationsViewsQueryKey(sectionId);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      await migrateLocalValuationViewsIfNeeded();
      const response = await listValuationsTableViews();
      return response.definitions
        .filter(tableView => matchesSection(tableView, sectionId))
        .map(fromValuationTableView);
    },
    staleTime: 30_000,
  });

  const views = useMemo(() => data ?? [], [data]);

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    [queryClient, queryKey]
  );

  const createMutation = useMutation({
    mutationFn: async (view: ValuationView) => {
      const created = await createValuationsTableView(
        toValuationTableViewCreatePayload(view, sectionId)
      );
      return fromValuationTableView(created);
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async (view: ValuationView) => {
      if (view.id === DEFAULT_VIEW_ID) {
        return view;
      }
      const updated = await updateValuationsTableView(
        view.id,
        toValuationTableViewUpdatePayload(view, sectionId)
      );
      return fromValuationTableView(updated);
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (viewId: string) => {
      if (viewId === DEFAULT_VIEW_ID) {
        return;
      }
      await deleteValuationsTableView(viewId);
    },
    onSuccess: invalidate,
  });

  const cloneMutation = useMutation({
    mutationFn: async ({ source, name }: { source: ValuationView; name: string }) => {
      const payload = toValuationTableViewCreatePayload({ ...source, id: 'temp', name }, sectionId);
      const created = await createValuationsTableView(payload);
      return fromValuationTableView(created);
    },
    onSuccess: invalidate,
  });

  const { mutateAsync: createMutateAsync } = createMutation;
  const { mutateAsync: updateMutateAsync } = updateMutation;
  const { mutateAsync: deleteMutateAsync } = deleteMutation;
  const { mutateAsync: cloneMutateAsync } = cloneMutation;

  const createView = useCallback(
    (view: ValuationView) => createMutateAsync(view),
    [createMutateAsync]
  );

  const updateView = useCallback(
    (view: ValuationView) => updateMutateAsync(view),
    [updateMutateAsync]
  );

  const deleteView = useCallback(
    (viewId: string) => deleteMutateAsync(viewId),
    [deleteMutateAsync]
  );

  const cloneView = useCallback(
    (source: ValuationView, name: string) => cloneMutateAsync({ source, name }),
    [cloneMutateAsync]
  );

  return {
    views,
    isLoading: isLoading || isFetching,
    isReady: !isLoading,
    refetch,
    createView,
    updateView,
    deleteView,
    cloneView,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
